import { useGetVideoSegmentationData } from '@/features/user/api/queries';
import { useAnalytics } from '@/lib/analytics';
import { formatSecondsToHHMMSS } from '@/lib/utils';
import { Skeleton } from '@/ui/skeleton';

const TranscriptsSkeleton = () => {
  return (
    <div className="md:w-100 flex-shrink-0 h-full">
      <div className="h-full rounded-2xl border border-gray-200 bg-white p-4">
        <Skeleton size="md" width="lg" className="mb-4" />
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, index) => (
            <div key={index} className="flex items-center gap-3 px-2 py-2">
              <Skeleton shape="circle" className="size-2" />
              <div className="flex flex-1 flex-row gap-2">
                <Skeleton size="sm" width="sm" />
                <Skeleton size="sm" width="full" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const TranscriptsError = () => {
  return (
    <div className="md:w-100 flex-shrink-0">
      <div className="h-fit rounded-2xl border border-gray-200 bg-white p-4">
        <p className="text-sm text-alert-red">
          There was an issue loading the segmentations. Please try again later.
        </p>
      </div>
    </div>
  );
};

const TranscriptsEmpty = () => {
  return (
    <div className="md:w-100 flex-shrink-0 h-full">
      <div className="h-full rounded-2xl border border-gray-200 bg-white p-4">
        <h3 className="mb-2 font-semibold text-gray-900">Time Stamps</h3>
        <p className="text-sm text-gray-500">
          No segmentations available right now for this session.
        </p>
      </div>
    </div>
  );
};

type TranscriptsProps = {
  sessionId: string;
  handleJumpToTime: (time: number) => void;
  getCurrentVideoTime?: () => number;
  hideForNoSegments?: boolean;
};

const Transcripts = ({
  sessionId,
  handleJumpToTime,
  getCurrentVideoTime,
  hideForNoSegments = true,
}: TranscriptsProps) => {
  const { segments, isLoading, isError } =
    useGetVideoSegmentationData(sessionId);
  const { trackEvent } = useAnalytics();

  const handleSegmentClick = (segment: {
    start_time: number;
    title: string;
  }) => {
    const currentTime = getCurrentVideoTime ? getCurrentVideoTime() : 0;

    // Track skip ahead if user is jumping forward
    if (segment.start_time > currentTime) {
      trackEvent('clickedSegmentSkipAhead', 1, {
        recordingId: sessionId,
        segmentTitle: segment.title,
        currentTime,
        targetTime: segment.start_time,
      });
    }

    // Jump to the segment time
    handleJumpToTime(segment.start_time);
  };

  if (isLoading) {
    return <TranscriptsSkeleton />;
  }

  if (isError) {
    if (hideForNoSegments) {
      return <></>;
    }
    return <TranscriptsError />;
  }

  if (segments?.length === 0) {
    if (hideForNoSegments) {
      return <></>;
    }
    return <TranscriptsEmpty />;
  }

  return (
    <div className="h-96 lg:w-100 flex-shrink-0">
      <div className="bg-white rounded-2xl border border-gray-200 p-4 h-full overflow-y-scroll">
        <h3 className="font-semibold text-gray-900 mb-4">Time Stamps</h3>
        <div className="space-y-3">
          <div className="space-y-3">
            {segments?.map((segment, index) => (
              <button
                key={index}
                className="flex items-center gap-3 py-2 w-full text-left hover:bg-magical-moonlight-l rounded-lg px-2 transition-colors"
                onClick={() => handleSegmentClick(segment)}
              >
                <div className="w-2 h-2 rounded-full bg-teal-500"></div>
                <div className="flex flex-row gap-2">
                  <div className="text-sm font-medium text-magical-moonlight-d4 flex items-center min-w-12">
                    {formatSecondsToHHMMSS(segment.start_time)} -
                  </div>
                  <div className="text-sm text-magical-moonlight-d3 max-w-64">
                    {segment.title}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Transcripts;
